import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Table, Button, Space, message, Popconfirm, Input } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import CreateParticipantModal from '../CreateParticipantModal';
import { ParticipantService } from '@/services/participant.service';
import apiService from '@/services/api.service';
import type { Participant } from '@/types/models';
import { logger } from '@/utils/logger';
import { formatDate } from '@/utils/date';
import { API_ENDPOINTS } from '@/constants/api.constants';

const ParticipantsPage: React.FC = () => {
    const location = useLocation();
    const [participants, setParticipants] = useState<Participant[]>([]);
    const [isParticipantsLoading, setIsParticipantsLoading] = useState(false);
    const [isCreateParticipantModalVisible, setIsCreateParticipantModalVisible] = useState(false);
    const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);

    const fetchParticipants = async () => {
        try {
            setIsParticipantsLoading(true);
            const response = await ParticipantService.getAllParticipants();
            setParticipants(response.data);
        } catch (error) {
            logger.error('Error fetching participants:', error);
            message.error('Ошибка при загрузке списка участников');
            setParticipants([]);
        } finally {
            setIsParticipantsLoading(false);
        }
    };

    const handleDeleteParticipant = async (id: number) => {
        try {
            logger.info('Attempting to delete participant', { id });
            const response = await apiService.delete(`${API_ENDPOINTS.PARTICIPANTS.BASE}/${id}`);
            logger.info('Participant deleted successfully', { id, response: response.data });
            message.success('Спортсмен успешно удален');
            await fetchParticipants();
        } catch (error: any) {
            logger.error('Error deleting participant:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'Ошибка при удалении спортсмена';
            message.error(errorMessage);
        }
    };

    useEffect(() => {
        if (location.pathname === '/admin/participants') {
            fetchParticipants();
        }
    }, [location.pathname]);

    const participantColumns: ColumnsType<Participant> = [
        {
            title: 'ФИО',
            key: 'fullName',
            render: (_: unknown, record: Participant) => 
                `${record.lastName} ${record.firstName}${record.middleName ? ` ${record.middleName}` : ''}`,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                        placeholder="Поиск по ФИО"
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                    />
                    <Space>
                        <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
                            Поиск
                        </Button>
                        <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
                            Сброс
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value, record) => {
                const fullName = `${record.lastName} ${record.firstName}${record.middleName ? ` ${record.middleName}` : ''}`;
                return fullName.toLowerCase().includes(String(value).toLowerCase());
            },
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                        placeholder="Поиск по email"
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                    />
                    <Space>
                        <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
                            Поиск
                        </Button>
                        <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
                            Сброс
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value, record) =>
                record.email?.toLowerCase().includes(String(value).toLowerCase()) ?? false,
        },
        {
            title: 'Дата рождения',
            dataIndex: 'dateOfBirth',
            key: 'dateOfBirth',
            render: (date: string) => date ? formatDate(date) : '-',
            sorter: (a, b) => {
                const dateA = new Date(a.dateOfBirth || 0).getTime();
                const dateB = new Date(b.dateOfBirth || 0).getTime();
                return dateA - dateB;
            },
        },
        {
            title: 'Пол',
            dataIndex: 'gender',
            key: 'gender',
            filters: [
                { text: 'Мужской', value: 'male' },
                { text: 'Женский', value: 'female' },
            ],
            onFilter: (value, record) => record.gender === value,
            render: (gender: string) => gender === 'male' ? 'Мужской' : 'Женский',
        },
        {
            title: 'Вес',
            dataIndex: 'weight',
            key: 'weight',
            render: (weight: number) => weight ? `${weight} кг` : '-',
            sorter: (a, b) => (a.weight || 0) - (b.weight || 0),
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: unknown, record: Participant) => (
                <Space>
                    <Button type="link" onClick={() => {
                        setSelectedParticipant(record);
                        setIsCreateParticipantModalVisible(true);
                    }}>
                        Редактировать
                    </Button>
                    <Popconfirm
                        title="Удалить спортсмена?"
                        description="Это действие нельзя отменить"
                        onConfirm={() => handleDeleteParticipant(record.id)}
                        okText="Да"
                        cancelText="Нет"
                    >
                        <Button type="link" danger>
                            Удалить
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16 }}>
                <Space>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setSelectedParticipant(null);
                            setIsCreateParticipantModalVisible(true);
                        }}
                    >
                        Добавить спортсмена
                    </Button>
                </Space>
            </div>
            <Table
                columns={participantColumns}
                dataSource={participants}
                rowKey="id"
                loading={isParticipantsLoading}
                pagination={{
                    showSizeChanger: true,
                    showTotal: (total) => `Всего ${total} спортсменов`,
                }}
            />
            <CreateParticipantModal
                visible={isCreateParticipantModalVisible}
                participant={selectedParticipant}
                onCancel={() => {
                    setIsCreateParticipantModalVisible(false);
                    setSelectedParticipant(null);
                }}
                onSuccess={() => {
                    setIsCreateParticipantModalVisible(false);
                    setSelectedParticipant(null);
                    fetchParticipants();
                }}
            />
        </div>
    );
};

export default ParticipantsPage;


